/**
 * Slice 5 — Centralised tooltip copy for the universal fallback presentation.
 *
 * Every database picker surface (OE tree, Table Explorer dropdown, schema/data
 * compare pickers, security manager, table designer, etc.) renders the same
 * `?` indicator on each fallback entry. The tooltip text is shared from this
 * one place so the wording can't drift between surfaces.
 *
 * Important: tooltip copy MUST NOT include the database name — the entry
 * already shows it, and duplicating would add nothing while leaking PII to
 * any future log/screenshot pipeline.
 */

export type FallbackEntryKind = 'config' | 'known' | 'unlisted';

/**
 * Copy displayed when the user hovers / focuses a fallback database entry.
 * `kind === 'config'` means the connection's configured `database` field;
 * `kind === 'known'` means an entry from the per-connection `knownDatabases`
 * LRU we appended to in a prior session; `kind === 'unlisted'` (#91) means the
 * listing SUCCEEDED and this entry — the picker's own current value — was not
 * in it, so it is rendered anyway to stop the trigger painting blank.
 *
 * The `'unlisted'` wording deliberately does not reuse the other two: they say
 * "listing failed", which is the opposite of what happened here.
 */
export function getUnverifiedDatabaseTooltip(kind: FallbackEntryKind): string {
  if (kind === 'config') {
    return "Presence not verified — listing failed. This is the database configured on the connection.";
  }
  if (kind === 'unlisted') {
    return "Not in this server's database list — shown because it is what's currently selected.";
  }
  return "Presence not verified — listing failed. This database is shown because you've used it on this connection before.";
}

/**
 * The single explainer string shown in the banner / sub-line / inline notice.
 * Callers compose layout (icon position, button row) but never paraphrase the
 * sentence.
 */
export const FALLBACK_BANNER_HEADLINE =
  "Couldn't list databases on this server — showing entries you've used before.";

/**
 * One-liner shown in place of `[Send diagnostic report]` when the user has
 * disabled crash reporting under Privacy settings.
 */
export const FALLBACK_PRIVACY_NOTICE =
  'Diagnostic reports are disabled in Privacy settings.';
